import { EVENTS, publish, subscribe } from '@sendible/event-manager';
import { Sidebar } from '@sendible/design-system';
import { useBridgeContext } from '@sendible/shared-state-bridge';
import { useLocation } from 'react-router-dom';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { routeUtils } from '@sendible/common';
import { ReportsSidebar } from './components/ReportsSidebar';
import { SidebarWrapper } from './index.styles';
import { getRoutes, SideBarConfigurationType, pages, PageBaseKeys } from '../../routes';

export const LegacySideBar = () => {
  const location = useLocation();
  const [responsiveIsOpen, setResponsiveIsOpen] = useState(false);
  const { t } = useTranslation('common_terms');
  const [{ user }] = useBridgeContext();

  const { activeIndex, isReports, sidebarConfig } = useMemo(() => {
    const { pathname } = location;
    const pathWihtoutSubPaths = routeUtils.getPathWithoutSubPaths(pathname);
    const currentRoutes = getRoutes();
    const routeConfigObject = currentRoutes[pathWihtoutSubPaths as PageBaseKeys];

    if (!routeConfigObject) {
      return {
        activeIndex: 0,
        isReports: false,
        sidebarConfig: null as SideBarConfigurationType | null,
      };
    }

    const { sidebar: sidebarConfigObject } = routeConfigObject;
    const { links, header } = sidebarConfigObject;
    const countLabels = user.countLabels as Record<string, number | undefined> | undefined;
    let computedActiveIndex = 0;

    const sidebarOptions = links.map(({ route, label, isBeta, countLabel, defaultCountValue }, index) => {
      if (route === pathWihtoutSubPaths) {
        computedActiveIndex = index;
      }

      const linkedRouteConfig = currentRoutes[route as PageBaseKeys];
      const count = countLabel ? (countLabels?.[countLabel] ?? defaultCountValue) : undefined;

      return {
        count,
        featureRestriction: linkedRouteConfig?.featureRestriction,
        isBeta,
        label: t(label),
        route,
      };
    });

    return {
      activeIndex: computedActiveIndex,
      isReports: pathWihtoutSubPaths === pages.Measure.base,
      sidebarConfig: {
        header: t(header),
        links: sidebarOptions,
      },
    };
  }, [location, t, user.countLabels]);

  useEffect(() => {
    const unsubscribe = subscribe(EVENTS.LEGACY_SIDEBAR_SHOW, (isOpen) => {
      setResponsiveIsOpen(isOpen);
    });

    return () => unsubscribe();
  }, []);

  // Hide sidebar entirely for the new reports page (full-screen editor)
  const { pathname } = location;

  if (pathname.startsWith(pages.Reporting.base)) {
    return null;
  }

  if (!sidebarConfig) return null;

  if (isReports) {
    return (
      <SidebarWrapper
        $isReports
        $responsiveIsOpen={responsiveIsOpen}
      >
        <ReportsSidebar responsiveIsOpen={responsiveIsOpen} />
      </SidebarWrapper>
    );
  }

  return (
    <SidebarWrapper $responsiveIsOpen={responsiveIsOpen}>
      <Sidebar
        activeIndex={activeIndex}
        header={sidebarConfig.header}
        onNavClick={(route) => publish(EVENTS.CHANGE_PAGE, route)}
        options={sidebarConfig.links}
        responsiveIsOpen={responsiveIsOpen}
      />
    </SidebarWrapper>
  );
};
